/**
 * Content production costs — the cost of generating each creative asset.
 *
 * This sits alongside creator fees on the "investment" side of the ROI page.
 * Beatly's finance team sets the base costs in SEK; we convert to the campaign
 * currency on the fly using the fxToSek table below.
 */

export type ContentMedium = 'video' | 'image';

/** Base production cost per content medium, denominated in SEK. */
export const PRODUCTION_COST_SEK: Record<ContentMedium, number> = {
  image: 1000,
  video: 6000,
};

/**
 * THE FX table in Omni (support #238): 1 unit of the given currency = N SEK, for every pay currency of the market list.
 * ECB reference rates 2026-11-03 (EUR/SEK 10.9515), converted to SEK; update by hand, keep identical in BO, Omni and cowork.
 * Production costs and the proposal builder read it. An unknown currency falls back to 1, i.e. counted as SEK.
 */
export const FX_TO_SEK: Record<string, number> = {
  SEK: 1,
  NOK: 1.061,
  DKK: 1.465,
  EUR: 10.95,
  GBP: 13.14,
  USD: 9.8,
  PLN: 2.478,
  CAD: 7.01,
};

/** Classify a product_type string into video / image. Stories default to image. */
export function classifyMedium(productType: string | null | undefined): ContentMedium {
  const t = (productType ?? '').toUpperCase();
  if (
    t.includes('REEL') ||
    t.includes('VIDEO') ||
    t.includes('TIKTOK') ||
    t.includes('YOUTUBE') ||
    t.includes('SHORT')
  ) {
    return 'video';
  }
  return 'image';
}

/** Per-publication production cost, in the campaign currency. */
export function productionCostForPublication(
  productType: string | null | undefined,
  campaignCurrency: string
): number {
  const medium = classifyMedium(productType);
  const sek = PRODUCTION_COST_SEK[medium];
  const rate = FX_TO_SEK[(campaignCurrency || 'SEK').toUpperCase()] ?? 1;
  // rate is "1 <currency> = N SEK", so dividing converts SEK → currency.
  return rate > 0 ? sek / rate : sek;
}
